import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend 
} from "recharts";
import { Briefcase, Users, Building2, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function ContratsPortefeuille() {
  const { token } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("type");

  useEffect(() => {
    const fetchPortfolio = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/contracts/stats/portfolio`, { headers });
        setPortfolio(res.data);
      } catch (error) {
        console.error("Error fetching portfolio:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPortfolio();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-80 skeleton rounded-lg"></div>
          <div className="h-80 skeleton rounded-lg"></div>
        </div>
      </div>
    );
  }

  const viewOptions = {
    type: { key: "by_type", title: "Par Type de Contrat", icon: Briefcase },
    produit: { key: "by_produit", title: "Par Produit", icon: Building2 },
    vendeur: { key: "by_vendeur", title: "Par Vendeur", icon: Users },
    revendeur: { key: "by_revendeur", title: "Par Revendeur", icon: Handshake }
  };

  const currentData = portfolio?.[viewOptions[view].key] || [];
  const pieData = currentData.map((item, i) => ({
    ...item,
    color: COLORS[i % COLORS.length]
  }));

  const totalMRR = currentData.reduce((sum, item) => sum + item.mrr, 0);

  return (
    <div className="space-y-6" data-testid="contrats-portefeuille">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Portefeuille Contrats</h1>
          <p className="text-muted-foreground text-sm">Répartition et analyse du portefeuille</p>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-48" data-testid="view-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="type">Par Type</SelectItem>
            <SelectItem value="produit">Par Produit</SelectItem>
            <SelectItem value="vendeur">Par Vendeur</SelectItem>
            <SelectItem value="revendeur">Par Revendeur</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {(() => {
                const Icon = viewOptions[view].icon;
                return <Icon className="w-4 h-4 text-primary" />;
              })()}
              {viewOptions[view].title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="mrr"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [formatCurrency(value), 'MRR']}
                      contentStyle={{ borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">MRR {viewOptions[view].title}</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pieData.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="mrr" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Table */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Détail {viewOptions[view].title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Nom</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Contrats</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">MRR</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">% du MRR</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">CA Leaser</th>
                </tr>
              </thead>
              <tbody>
                {pieData.map((item, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="font-medium text-foreground">{item.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 font-mono">{item.count}</td>
                    <td className="text-right py-3 px-4 font-mono font-bold">{formatCurrency(item.mrr)}</td>
                    <td className="text-right py-3 px-4 font-mono">
                      {totalMRR > 0 ? ((item.mrr / totalMRR) * 100).toFixed(1) : 0}%
                    </td>
                    <td className="text-right py-3 px-4 font-mono">{formatCurrency(item.ca_leaser)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-bold">
                  <td className="py-3 px-4">Total</td>
                  <td className="text-right py-3 px-4 font-mono">{pieData.reduce((s, i) => s + i.count, 0)}</td>
                  <td className="text-right py-3 px-4 font-mono">{formatCurrency(totalMRR)}</td>
                  <td className="text-right py-3 px-4 font-mono">100%</td>
                  <td className="text-right py-3 px-4 font-mono">{formatCurrency(pieData.reduce((s, i) => s + i.ca_leaser, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
